import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useConfig } from '@peracto/peracto-config'
import sortBy from 'lodash/sortBy'

const EditorContainer = styled.div`
    .ck-content {
        max-width: ${props => `${props.maxwidth}%`};
        margin: ${props => {
            switch (props.blockalignment) {
                case 'center':
                    return `0 auto`
                case 'right':
                    return `0 0 0 auto`
                default:
                    return `0 auto 0 0`
            }
        }};
    }
`

const TextControls = ({ isEditMode, onChange, state: { text, maxWidth, blockAlignment } }) => {
    const editorRef = useRef()
    const [editorLoaded, setEditorLoaded] = useState(false)
    const { CKEditor, InlineEditor } = editorRef.current || {}
    const config = useConfig()
    const { colorOptions, headings = [] } = config.get('editorConfig') || []

    const themeColours = colorOptions
        ? colorOptions.map(colour => ({
              label: colour.label,
              color: colour.value,
          }))
        : []

    const defaultHeadings =
        InlineEditor?.defaultConfig.heading.options.filter(opt => opt.model !== 'paragraph') || []
    const defaultParagraph =
        InlineEditor?.defaultConfig.heading.options.find(opt => opt.model === 'paragraph') || []

    const customHeadingModels = headings.map(heading => heading.model)

    const filteredDefaultHeadings = defaultHeadings.filter(
        heading => !customHeadingModels.includes(heading.model)
    )

    const combinedHeadings = [...filteredDefaultHeadings, ...headings]

    const sortedHeadings = sortBy(combinedHeadings, 'model')

    useEffect(() => {
        const { InlineEditor } = require('@peracto/ckeditor')

        const { CKEditor: CKEditorReact } = require('@ckeditor/ckeditor5-react')
        editorRef.current = {
            CKEditor: CKEditorReact,
            InlineEditor,
        }
        setEditorLoaded(true)
    }, [])

    return (
        editorLoaded && (
            <EditorContainer maxwidth={maxWidth} blockalignment={blockAlignment}>
                <CKEditor
                    editor={InlineEditor}
                    config={{
                        ...InlineEditor.defaultConfig,
                        placeholder: 'Enter your text here...',
                        fontColor: {
                            colors: [
                                ...themeColours,
                                // we need to include the defaults here otherwise they get removed :(
                                {
                                    color: 'hsl(0, 0%, 0%)',
                                    label: 'Black',
                                },
                                {
                                    color: 'hsl(0, 0%, 30%)',
                                    label: 'Dim grey',
                                },
                                {
                                    color: 'hsl(0, 0%, 60%)',
                                    label: 'Grey',
                                },
                                {
                                    color: 'hsl(0, 0%, 90%)',
                                    label: 'Light grey',
                                },
                                {
                                    color: 'hsl(0, 0%, 100%)',
                                    label: 'White',
                                    hasBorder: true,
                                },
                                {
                                    color: 'hsl(0, 75%, 60%)',
                                    label: 'Red',
                                },
                                {
                                    color: 'hsl(30, 75%, 60%)',
                                    label: 'Orange',
                                },
                                {
                                    color: 'hsl(60, 75%, 60%)',
                                    label: 'Yellow',
                                },
                                {
                                    color: 'hsl(90, 75%, 60%)',
                                    label: 'Light green',
                                },
                                {
                                    color: 'hsl(120, 75%, 60%)',
                                    label: 'Green',
                                },
                                {
                                    color: 'hsl(150, 75%, 60%)',
                                    label: 'Aquamarine',
                                },
                                {
                                    color: 'hsl(180, 75%, 60%)',
                                    label: 'Turquoise',
                                },
                                {
                                    color: 'hsl(210, 75%, 60%)',
                                    label: 'Light blue',
                                },
                                {
                                    color: 'hsl(240, 75%, 60%)',
                                    label: 'Blue',
                                },
                                {
                                    color: 'hsl(270, 75%, 60%)',
                                    label: 'Purple',
                                },
                            ],
                        },
                        heading: {
                            options: [defaultParagraph, ...sortedHeadings],
                        },
                    }}
                    data={text}
                    onChange={(_event, editor) => {
                        onChange({ text: editor.getData() })
                    }}
                    disabled={!isEditMode}
                />
            </EditorContainer>
        )
    )
}

export default TextControls
